import { motion } from "framer-motion";
import { useRef, useEffect } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { BUSINESS } from "../data";

export const Hero = ({ onBook }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // React does not reliably set the `muted` DOM property; set it explicitly
    // so mobile browsers (iOS Safari, Android Chrome) allow autoplay.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    // Retry once the video can play and when the tab/app regains focus
    v.addEventListener("canplay", tryPlay, { once: true });
    document.addEventListener("visibilitychange", tryPlay);
    return () => document.removeEventListener("visibilitychange", tryPlay);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#050505]"
    >
      {/* Background video */}
      <video
        ref={videoRef}
        data-testid="hero-video"
        className="absolute inset-0 z-0 w-full h-full object-cover"
        poster="/hero_poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
      >
        <source src="/hero.mp4" type="video/mp4" />
      </video>

      {/* Dark cinematic overlays for readability + luxury feel */}
      <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-r from-[#050505] via-[#050505]/75 to-[#050505]/30" />
      <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-7 backdrop-blur-sm"
          >
            <Sparkles size={14} className="text-white" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">
              {BUSINESS.tagline} · {BUSINESS.location}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[0.95]"
          >
            Premium Auto Detailing That Brings Your Car Back to Life
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-7 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
          >
            {BUSINESS.serving}. Show-quality results for daily drivers, luxury cars, and exotics.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <button
              data-testid="hero-book-btn"
              onClick={onBook}
              className="btn-3d gloss-sweep group inline-flex items-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-7 py-4 hover:bg-white"
            >
              Book Your Detail
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-3d-outline inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-medium px-7 py-4 hover:bg-white/5"
            >
              View Services
            </button>
          </motion.div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Scroll
        </span>
        <div className="h-10 w-[1px] bg-gradient-to-b from-white/60 to-transparent" />
      </motion.div>
    </section>
  );
};
